import * as App from "@timesheets/routes-app";
import * as CommonConstants from "@timesheets/constants-common";
import { ChargingPeriod, DateRelativeTo, EmailAddressesSetting, EntryPeriod, initialUpper } from "@gemma/utils";
import * as Querying from "@gemma/querying";

import { Environment } from "@gemma/configuration";
import { IWebApplication } from "@timesheets/webapp-support";
import { Sorters, TCustomData, TMapOne, TMappedApiArrayEntity, TMappedApiEntity, mapResolved } from "@gemma/api-browser";
import { icons, report } from "@/utils";

import Vue from "vue";
import { WebApplicationConstants } from "../../setup/WebApplicationConstants";
import { globalErrorHandler } from "../globalErrorHandler";
import { globalWarnHandler } from "../globalWarnHandler";
import { v1 } from "uuid";
import { IState } from "../../vuex/IState";
import Events from "../Events";
import { Store } from "vuex";
import { WireEntities } from "@timesheets/common";
import { Day } from "@gemma/dates";
import { MomentDateFacade } from "@gemma/dates-moment";
import { Position } from "../../utils/Position";
import { ITargetedListener } from "@gemma/eventemitter";

// const defaultMergeMethodsStrategy = Vue.config.optionMergeStrategies.methods;

export function vuePrototype(webApplication: IWebApplication) {
  if (WebApplicationConstants.environment === Environment.Production) {
    Vue.config.productionTip = false;
  }

  Vue.config.errorHandler = globalErrorHandler;
  Vue.config.warnHandler = globalWarnHandler;

  // Vue.config.optionMergeStrategies.methods = function strategy(baseVal: RecordAny, derivedVal: RecordAny, vm: Vue & { noInherit?: boolean }) {
  //   if (derivedVal && baseVal && (!vm || !vm.noInherit)) {
  //     const merged: RecordAny = derivedVal;
  //     const mergableKeys = Object.keys(derivedVal).filter((derivedValKey: string) => baseVal[derivedValKey]);
  //     for (const key of mergableKeys) {
  //       const derived = derivedVal[key];
  //       const base = baseVal[key];

  //       if (derived !== base) {
  //         merged[key] = function() {
  //           const baseResult = base.call(this, ...arguments);
  //           const args = [...arguments, baseResult];
  //           const result = derived.call(this, ...args);
  //           return result;
  //         };
  //       }
  //     }

  //     for (const key in baseVal) {
  //       if (!derivedVal[key]) {
  //         merged[key] = baseVal[key];
  //       }
  //     }

  //     return merged;
  //   }

  //   return defaultMergeMethodsStrategy(baseVal, derivedVal);
  // };

  Vue.prototype.$app = webApplication;
  Vue.prototype.$Constants = CommonConstants;
  Vue.prototype.$utils = {
    ChargingPeriod,
    EntryPeriod,
    MomentDateFacade,
    Querying,
    initialUpper,
    Position,
  };

  Vue.prototype.$Events = Events;
  Vue.prototype.$icons = icons;

  Vue.prototype.$Currency = {
    GBP: {
      WithCurrencySymbol: new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }),
      AsCurrencyNumber: new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2 }),
      format(value: string, withSymbol = true) {
        return withSymbol ? Vue.prototype.$Currency.GBP.WithCurrencySymbol.format(value) : parseFloat(Vue.prototype.$Currency.GBP.AsCurrencyNumber.format(value));
      },
    },
  };

  Vue.prototype.$report = report;
  Vue.prototype.$site = {
    Routes: App,
  };

  Vue.prototype.map = function<
    //
    TEntity extends TMappedApiEntity<TMapped>,
    TMapped extends TMappedApiEntity<TMapped>,
    TResult extends TMappedApiArrayEntity<TMapped>,
    CustomData extends TCustomData<TEntity, TMapped>
  >(
    //
    this: Vue & { mapOne: TMapOne<Vue, TEntity, TMapped, CustomData> },
    items: TEntity,
    mapOne: TMapOne<Vue, TEntity, TMapped, CustomData>,
    options?: { customData?: CustomData; copyByDefault?: boolean; sorters?: Sorters }
  ): TResult {
    return mapResolved.call(this, items, (mapOne || this.mapOne) as any, options) as TResult;
  };

  Vue.mixin({
    props: {
      $pageId: {
        type: String,
        required: false,
        default() {
          const segments = window.location.pathname.split("/");
          const pageId = segments[segments.length - 1];

          return pageId;
        },
      },
    },
    data() {
      this.$id = v1();

      return {
        isLoading: true,
        isMounted: false,
      };
    },
    computed: {
      currentRole(this: Vue) {
        return (<IState>this.$store.state).user.currentRole;
      },
      currentSession(this: Vue) {
        return (<IState>this.$store.state).currentSession;
      },
      roles(this: Vue) {
        return (<IState>this.$store.state).user.roles;
      },
      disabledRoles(this: Vue): WireEntities.Roles.IRecruitmentAgencyRole[] {
        return (<Store<IState>>this.$store).getters["user/disabledRoles"];
      },
      enabledRoles(this: Vue): WireEntities.Roles.IRecruitmentAgencyRole[] {
        return (<Store<IState>>this.$store).getters["user/enabledRoles"];
      },
      recruitmentAgencySettings(this: Vue): WireEntities.RecruitmentAgency.IRecruitmentAgencySettings {
        if (this.currentRole) {
          return this.currentRole.recruitmentAgency.settings;
        }

        return {
          colors: {
            primary: {
              main: null,
              text: null,
            },
            secondary: {
              main: null,
              text: null,
            },
          },

          contractorFinancials: {
            enabled: false,
          },

          customisation: {
            contractor: {
              name: {
                singular: {
                  initialUpper: "Contractor",
                  lower: "contractor",
                },
                plural: {
                  initialUpper: "Contractors",
                  lower: "contractors",
                },
              },
            },
          },

          defaults: {
            applyDefaultWorkingTime: true,
            dateRelativeTo: DateRelativeTo.Beginning,
            contractors: {
              allowHolidays: true,
              allowNightshifts: true,
              chargingPeriod: CommonConstants.DefaultContractorsChargingPeriod(),
              entryPeriod: EntryPeriod.NoDefaultSet,
            },
            timesheetWeekStartsOn: Day.Monday,
          },

          documents: {
            allowUploads: false,
            allowByDefault: true,
          },

          // due: {
          //   submissions: {
          //     calendarMonthlyDue: {
          //       type: CommonConstants.Submissions.RemindMonthly.FixedDate,
          //       dueDate: 1,
          //     },
          //     weeklyDue: {
          //       type: CommonConstants.Submissions.RemindWeekly.DayOfTheWeek,
          //       dueDay: Days.Monday,
          //     },
          //   },
          //   authorisations: undefined,
          // },

          emails: {
            emailAcceptedTimesheetCopiesTo: new EmailAddressesSetting.EmailAddressesSetting(),
            replyTo: new EmailAddressesSetting.EmailAddressesSetting(),
          },

          hourFormat: { decimal: false },

          logos: {
            rectangular: false,
            square: false,
          },

          portalUrl: {
            internal: "",
          },
        };
      },
      isContractingRole(this: any) {
        return this.currentRole && this.currentRole.type === CommonConstants.Roles.ContractingRole.TypeName;
      },
      isAuthorisingRole(this: any) {
        return this.currentRole && this.currentRole.type === CommonConstants.Roles.AuthorisingRole.TypeName;
      },
      isRecruitmentAgencyAdministrationRole(this: any) {
        return this.currentRole && this.currentRole.type === CommonConstants.Roles.RecruitmentAgencyAdministrationRole.TypeName;
      },
      isSuperAdministrator(this: any) {
        return this.isRecruitmentAgencyAdministrationRole && this.currentRole.administrator.isSuperAdministrator;
      },
      allComponentsLoaded(this: { componentNames: Record<string, boolean> }) {
        if (!this.componentNames) {
          return true;
        }

        const allComponentsLoaded = Object.keys(this.componentNames).reduce((p, c) => p && this.componentNames[c], true);

        return allComponentsLoaded;
      },
      readyToDisplay() {
        return this.isMounted && this.allComponentsLoaded;
      },
    },
    watch: {
      readyToDisplay(this: Vue, newValue: boolean) {
        if (newValue) {
          this.$emit("loadingComplete");
        }
      },
      async route(
        this: Vue & {
          isLoading: boolean;
          load?: () => Promise<void>;
        },
        newRoute
      ) {
        if (newRoute && typeof this.load === "function") {
          this.isLoading = true;
          await this.load();
          this.$emit("loadingComplete");
        }
      },
    },
    created(this: Vue & { onLoadingComplete: () => void }) {
      this.$on("loadingComplete", this.onLoadingComplete);
    },
    mounted(this: { isMounted: boolean }) {
      this.isMounted = true;
    },
    destroyed(this: Vue & { onLoadingComplete: () => void }) {
      WebApplicationConstants.eventBus.removeFilters(this);
      WebApplicationConstants.listenerEventBuses.system.off(undefined, undefined, this, targetCheck(this));
      WebApplicationConstants.listenerEventBuses.application.off(undefined, undefined, this, targetCheck(this));
      this.$app.liveUpdateBus.off(undefined, undefined, this, targetCheck(this));
      this.$off("loadingComplete", this.onLoadingComplete);
    },
    methods: {
      onLoadingComplete() {
        Vue.nextTick(() => {
          this.isLoading = false;
        });
      },
    },
  });
}

const targetCheck = (compareWith: any) => (target: ITargetedListener<any, unknown, any>) => {
  return target.target && target.target._uid === compareWith._uid;
};
